import ErrorComponent from "@/components/atoms/ErrorComponent";
import Loader from "@/components/atoms/Loader";
import { CardEditProps } from "@/interfaces/Card";
import Organisation, {
  UpdateOrganisationForm,
} from "@/interfaces/Organisation";
import {
  useGetOrganisationQuery,
  useUpdateOrganisationMutation,
} from "@/services/organisations";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { FC, useEffect } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import { SubmitErrorHandler, SubmitHandler, useForm } from "react-hook-form";
import { ToastContentProps, toast } from "react-toastify";
import CardHeader from "../../card-headers/CardHeader";

const OrganisationEditCard: FC<CardEditProps<Organisation>> = ({ id }) => {
  const { data, isLoading, isError, error } = useGetOrganisationQuery(id);
  const [updateOrganisationAsync] = useUpdateOrganisationMutation();

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<UpdateOrganisationForm>();

  useEffect(() => {
    if (data) {
      reset({
        name: data.name,
        backgroundColor: data.backgroundColor,
        textColor: data.textColor,
        convention: null,
        catalogUrl: data.catalogUrl || "",
      });
    }
  }, [data, reset]);

  const onSubmit: SubmitHandler<UpdateOrganisationForm> = async (data) => {
    toast.promise(updateOrganisationAsync({ id, data }).unwrap(), {
      pending: "Modification de l'organisation en cours ...",
      success: {
        render() {
          return (
            <p className="mb-0 text-center">
              Organisation modifiée avec succés
            </p>
          );
        },
      },
      error: {
        render({ data }: ToastContentProps<FetchBaseQueryError>) {
          if (data?.status === 422) {
            return "Les champs remplis semblent incorrects.";
          }
          return "Une erreur est survenue";
        },
      },
    });
  };

  const onError: SubmitErrorHandler<UpdateOrganisationForm> = () => {
    toast.error("Veuillez vérifier les champs du formulaire");
  };

  if (isLoading) {
    return <Loader />;
  }

  if (isError) {
    return <ErrorComponent error={error} absolute returnButton />;
  }

  return (
    <Card>
      <CardHeader title="Modifier l'organisation" />
      <Card.Body>
        <Form onSubmit={handleSubmit(onSubmit, onError)}>
          <Row className="g-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Nom de l'organisation</Form.Label>
                <Form.Control
                  type="text"
                  {...register("name", {
                    required: "Le nom est requis",
                    minLength: {
                      value: 2,
                      message: "Le nom doit contenir au moins 2 caract�res",
                    },
                  })}
                  isInvalid={!!errors.name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Couleur de fond</Form.Label>
                <Form.Control
                  type="color"
                  {...register("backgroundColor", {
                    required: "La couleur de fond est requise",
                  })}
                  isInvalid={!!errors.backgroundColor}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.backgroundColor?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Couleur du texte</Form.Label>
                <Form.Control
                  type="color"
                  {...register("textColor", {
                    required: "La couleur du texte est requise",
                  })}
                  isInvalid={!!errors.textColor}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.textColor?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Lien du catalogue :</Form.Label>
                <Form.Control
                  type="url"
                  placeholder="https://exemple.com/catalogue"
                  isInvalid={!!errors.catalogUrl}
                  {...register("catalogUrl", {
                    pattern: {
                      value: /^https?:\/\/.+/,
                      message: "L'URL doit commencer par http:// ou https://",
                    },
                  })}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.catalogUrl?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Convention PDF :</Form.Label>
                <Form.Control
                  type="file"
                  accept=".pdf"
                  {...register("convention")}
                  isInvalid={!!errors.convention}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.convention?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={12}>
              <Button type="submit" variant="primary">
                Enregistrer les modifications
              </Button>
            </Col>
          </Row>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default OrganisationEditCard;
